import { ccfRoutes } from '@app/ccf/ccf-routes';
import Page from '@app/components/layout/page/page';
import { useDownloadScope1And2Template } from '@shared/api/hooks/ccf/imports';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import GuideTemplate from '@shared/components/guide-template';
import { useToast } from '@shared/components/ui/use-toast';
import { useState } from 'react';
import {
  Navigate,
  generatePath,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { getScope1And2Config } from '../config';

const TEMPLATE_FILENAME = 'scope-1-2-data-collection-template.xlsx';

export default function Scope1And2ImportProcessPage() {
  const navigate = useNavigate();
  const { report_uuid, category_slug } = useParams();
  const config = getScope1And2Config(category_slug);
  const { toast } = useToast();
  const { refetch } = useDownloadScope1And2Template();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!config) {
    return (
      <Navigate
        to={generatePath(ccfRoutes.COMPANY_REPORT, { report_uuid })}
        replace
      />
    );
  }

  const goToUpload = () =>
    navigate(
      generatePath(ccfRoutes.SCOPE_1_2_IMPORT_UPLOAD, {
        report_uuid,
        category_slug,
      })
    );

  const handleDownloadTemplate = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const { data } = await refetch();
      if (!data) throw new Error('No template returned');

      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = TEMPLATE_FILENAME;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'Download failed',
        description:
          'We couldn’t generate your template. Please try again or get in touch if the problem persists.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Page name={`${config.label} - import`}>
      <GuideTemplate
        preTitle={`Import ${config.label.toLowerCase()}`}
        title="The import process"
        description={
          <Text>
            Our import tool is designed to make the process as simple as
            possible for you. Download{' '}
            <TextLink onClick={handleDownloadTemplate}>the template</TextLink>,
            fill in the <span className="font-semibold">{config.label}</span>{' '}
            sheet, then upload it here.
          </Text>
        }
        primaryActionProps={{
          onClick: goToUpload,
          'data-testid': 'ready-to-import-button',
        }}
        primaryActionText="I'm ready to import"
      >
        <div className="flex space-x-1 py-12">
          <div className="flex flex-1 flex-col items-center rounded-lg border border-secondary-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.download className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">
                Download the{' '}
                <TextLink onClick={handleDownloadTemplate}>Template</TextLink>
              </Text>
            </div>
            <Text>
              Fill in the {config.label} sheet, matching each site, unit and
              time period to the names used in the platform.
            </Text>
          </div>
          <div className="flex items-center justify-center">
            <Icons.chevronRight className="size-8 text-secondary-500" />
          </div>
          <div className="flex flex-1 flex-col items-center rounded-lg border border-secondary-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.upload className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">
                Upload your spreadsheet
              </Text>
            </div>
            <Text>
              Select the relevant columns to upload. We&apos;ll match your data
              to your platform records and flag anything that doesn&apos;t.
            </Text>
          </div>
          <div className="flex items-center justify-center">
            <Icons.chevronRight className="size-8 text-secondary-500" />
          </div>
          <div className="flex flex-1 flex-col items-center rounded-lg border border-secondary-200 bg-gray-00 px-4 py-6 shadow-lg">
            <div className="mb-3 flex items-center space-x-2">
              <Icons.check className="size-5 text-secondary-500" />
              <Text className="text-md font-medium">Import your data</Text>
            </div>
            <Text>
              Once everything is matched, your activities are added to the
              report instantly.
            </Text>
          </div>
        </div>
      </GuideTemplate>
    </Page>
  );
}
